import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import PhotoGrid from '../components/PhotoGrid';
import PhotoLightbox from '../components/PhotoLightbox';
import SEOHead from '../components/SEOHead';
import { getOptimizedUrl } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_LOGO = process.env.REACT_APP_DEFAULT_LOGO_URL;
const FACEBOOK_PROFILE = process.env.REACT_APP_FACEBOOK_PROFILE_URL;

// Helper function to get full image URL (for non-photo elements)
const getImageUrl = (path) => {
  if (!path) return '';
  return getOptimizedUrl(path, 'medium', 'desktop');
};

const AlbumDetail = () => {
  const { albumId } = useParams();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [settings, setSettings] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navNewsletterEmail, setNavNewsletterEmail] = useState('');
  const [isNavSubscribing, setIsNavSubscribing] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchAlbumData();
    trackVisitor();
  }, [albumId]);

  const trackVisitor = async () => {
    try {
      await axios.post(`${API}/visitor/track`);
      const statsRes = await axios.get(`${API}/visitor/stats`);
      setVisitorStats(statsRes.data);
    } catch (error) {
      console.error('Error tracking visitor:', error);
    }
  };

  const fetchAlbumData = async () => {
    try {
      const [albumsRes, photosRes, settingsRes] = await Promise.all([
        axios.get(`${API}/albums`),
        axios.get(`${API}/albums/${albumId}/photos`),
        axios.get(`${API}/settings`)
      ]);
      const currentAlbum = albumsRes.data.find(a => a.id === albumId);
      setAlbum(currentAlbum);
      setPhotos(photosRes.data);
      setSettings(settingsRes.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching album data:', error);
      setIsLoading(false);
    }
  };

  const handleNavNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsNavSubscribing(true);
    
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: navNewsletterEmail });
      toast.success('🎉 Bedankt voor je inschrijving! Je ontvangt nu alle updates.');
      setNavNewsletterEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsNavSubscribing(false);
    }
  };

  // Lightbox handlers
  const handlePhotoClick = useCallback((photo, index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const handleLightboxNavigate = useCallback((newIndex) => {
    setLightboxIndex(newIndex);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0a56c2]/20 border-t-[#0a56c2] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Album laden...</p>
        </div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <svg className="w-24 h-24 mx-auto mb-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Album niet gevonden</h2>
          <p className="text-gray-600 mb-6">Dit album bestaat niet of is verwijderd.</p>
          <Link 
            to="/fotos" 
            className="inline-flex items-center gap-2 bg-[#0a56c2] hover:bg-[#083d8a] text-white px-6 py-3 rounded-full font-semibold transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar foto's
          </Link>
        </div>
      </div>
    );
  }

  // Get SEO image - use cover image or first photo
  const seoImage = album.cover_image_url || (photos.length > 0 ? photos[0].image_url : null);
  const seoDescription = album.description || settings?.default_album_intro || `Bekijk de foto's van ${album.title}`;

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <SEOHead
        title={`${album.title} - Foto's`}
        description={seoDescription}
        image={seoImage}
        url={`${BACKEND_URL}/fotos/${albumId}`}
        type="article"
      />

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#0a56c2] shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <a href="/" className="cursor-pointer">
                <img 
                  src={getImageUrl(settings?.logo_url) || DEFAULT_LOGO}
                  alt="Feestcomité Leupegem logo - Terug naar home" 
                  className="h-16 w-auto sm:h-20"
                />
              </a>
            </div>
            <div className="hidden md:flex gap-8">
              <a href="/" className="text-white hover:text-[#ffe980] font-medium transition-colors">Home</a>
              <a href="/fotos" className="text-white hover:text-[#ffe980] font-medium transition-colors">Foto's</a>
            </div>
            <div className="flex items-center gap-4">
              {/* Newsletter Icon - Desktop only */}
              <div className="hidden md:block relative">
                <button 
                  className="newsletter-icon relative text-white hover:text-[#ffe980] transition-colors p-2"
                  aria-label="Nieuwsbrief"
                >
                  <div className="relative">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#ffe980] rounded-full pulse-glow"></span>
                  </div>
                </button>
                
                <div className="newsletter-dropdown absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl p-6 border-2 border-[#ffe980] z-50">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">📬 Nieuwsbrief</h3>
                  <p className="text-sm text-gray-600 mb-4">Mis geen enkel evenement!</p>
                  <form onSubmit={handleNavNewsletterSubmit}>
                    <input 
                      type="email" 
                      value={navNewsletterEmail}
                      onChange={(e) => setNavNewsletterEmail(e.target.value)}
                      placeholder="jouw@email.be"
                      required
                      disabled={isNavSubscribing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-[#0a56c2] disabled:opacity-50"
                    />
                    <button 
                      type="submit"
                      disabled={isNavSubscribing}
                      className="w-full bg-[#0a56c2] text-white py-2 rounded-lg hover:bg-[#083d8a] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isNavSubscribing ? 'Bezig...' : 'Inschrijven'}
                    </button>
                  </form>
                </div>
              </div>
              
              <a href="/admin" className="hidden sm:block text-sm text-white/80 hover:text-white transition-colors">Admin</a>
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-white p-2 rounded-md hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#083d8a] border-t border-white/10 animate-in slide-in-from-top-2 duration-200">
            <div className="px-4 py-2 space-y-1">
              <a 
                href="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-white hover:text-[#ffe980] hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
              >
                Home
              </a>
              <a 
                href="/fotos" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-white hover:text-[#ffe980] hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
              >
                Foto's
              </a>
              <div className="border-t border-white/10 mt-2 pt-2">
                <a 
                  href="/admin" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-white/70 hover:text-white hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
                >
                  Admin
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Album Header */}
      <section className="pt-32 pb-8 sm:pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-yellow-50 to-white">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/fotos" 
            className="inline-flex items-center text-[#0a56c2] hover:text-[#083d8a] font-medium mb-6 group"
          >
            <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar albums
          </Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4" data-testid="album-title">
            {album.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-4">
            <span className="inline-flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {album.date}
            </span>
            <span className="inline-flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {photos.length} foto's
            </span>
          </div>
          {album.description && (
            <p className="text-gray-700 max-w-3xl">{album.description}</p>
          )}
        </div>
      </section>

      {/* Photos Grid Section */}
      <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <PhotoGrid
            photos={photos}
            initialBatchSize={24}
            batchSize={24}
            onPhotoClick={handlePhotoClick}
            albumTitle={album.title}
          />
        </div>
      </section>

      {/* Lightbox */}
      <PhotoLightbox
        isOpen={lightboxOpen}
        photos={photos}
        currentIndex={lightboxIndex}
        onClose={handleLightboxClose}
        onNavigate={handleLightboxNavigate}
        albumTitle={album.title}
      />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <img 
              src={getImageUrl(settings?.logo_url) || DEFAULT_LOGO}
              alt="Feestcomité Leupegem logo" 
              className="h-24 sm:h-32 w-auto mx-auto mb-4 brightness-0 invert"
            />
          </div>
          <p className="text-gray-400 mb-4">{settings?.footer_tagline || 'Feestcomité Leupegem - Waar elk feest onvergetelijk wordt'}</p>
          
          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6 mb-6 text-sm text-gray-400">
            <a 
              href={`mailto:${settings?.contact_email || 'leupegemfeestcomite@gmail.com'}`}
              className="hover:text-white transition-colors inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {settings?.contact_email || 'leupegemfeestcomite@gmail.com'}
            </a>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <span className="inline-flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {settings?.contact_address || 'Dries ter Biest 23, 9700 Oudenaarde'}
            </span>
          </div>
          
          <div className="flex justify-center gap-6 mb-6">
            <a 
              href={settings?.facebook_url || FACEBOOK_PROFILE}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Volg ons op Facebook"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>
          <p className="text-sm text-gray-500">© {new Date().getFullYear()} Feestcomité Leupegem. Alle rechten voorbehouden.</p>
          <p className="text-xs text-gray-600 mt-4">
            {settings?.footer_credit_text || 'Met veel liefde'} <span className="text-red-500">❤️</span> gemaakt door{' '}
            <a 
              href={settings?.footer_credit_url || "https://www.websitebestellen.be"} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-300 hover:underline transition-colors"
            >
              {settings?.footer_credit_name || 'WebsiteBestellen.be'}
            </a>
          </p>
          {visitorStats && (
            <div className="mt-8 pt-6 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-2">Bezoekers</p>
              <div className="flex justify-center gap-4 sm:gap-6 flex-wrap text-sm">
                <div>
                  <span className="text-gray-400">Vandaag: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_today}</span>
                </div>
                <div>
                  <span className="text-gray-400">Deze maand: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_this_month}</span>
                </div>
                <div>
                  <span className="text-gray-400">Dit jaar: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_this_year}</span>
                </div>
                <div>
                  <span className="text-gray-400">Totaal: </span>
                  <span className="text-white font-semibold">{visitorStats.total_visits}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

export default AlbumDetail;
